import { AppstoreOutlined, DownloadOutlined, LoadingOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { ApiClient } from '@api/api-configuration';
import { Panel } from '@app/features/dashboard/components';
import { useQuery } from '@tanstack/react-query';
import { Alert, Image } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ATTACHMENT_BUCKET_STYLE as BUCKET_STYLE } from '@app/features/attachments/attachmentKind';

import { formatDate } from './clientCard.format';
import { folderFileKey, isFolderImage } from './folderFile';
import useFolderDownload from './useFolderDownload';
import useFolderPreview from './useFolderPreview';
import { useInView } from './useInView';
import {
  CardAttachment,
  CardVisit,
  FolderBucket,
  FOLDER_BUCKET_LABEL_KEYS,
  FolderFile,
  buildFolder,
  folderBucketCounts,
} from './clientCard.model';

/** The "all" chip has no bucket of its own, so it keeps the card's own accent. */
const ALL_CHIP_COLOR = 'rgb(2 132 199)';

/** One visit's attachments. Shared by every tile from that visit. */
const VISIT_ATTACHMENTS_QUERY_KEY = 'client-card-visit-attachments';

/** A visit holds a handful of files; one page is all of them. */
const VISIT_ATTACHMENTS_PAGE_SIZE = 100;

/**
 * A visit's attachments, fetched once per visit and shared by every tile from it.
 *
 * WHY THIS FETCH EXISTS. The visits list inlines each attachment's metadata but not
 * its thumbnail, and putting one there would mean a base64 256x256 JPEG per file
 * inside a response that already carries hundreds of visits. The per-visit endpoint
 * returns them, so the grid asks for them a visit at a time, and only for the tiles
 * a reader actually scrolled to. React Query dedupes on the key, so a visit with
 * four photos still costs one request.
 *
 * Files uploaded against the client record already carry their thumbnail from the
 * list that fetched them, and never reach this.
 */
const useVisitAttachmentThumbnail = (file: FolderFile, enabled: boolean): string | null => {
  const visitId = file.source.type === 'visit' ? file.source.visitId : null;

  const { data } = useQuery({
    queryKey: [VISIT_ATTACHMENTS_QUERY_KEY, visitId],
    enabled: enabled && visitId !== null,
    // The images do not change while a card is open, and the same visit's tiles are
    // mounted and unmounted as the reader switches filters.
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const response = await ApiClient.visitsApi.visits.listVisitAttachments(visitId as number, {
        offset: 0,
        limit: VISIT_ATTACHMENTS_PAGE_SIZE,
      });
      return (response.data?.data ?? []) as unknown as CardAttachment[];
    },
  });

  return data?.find((one) => one.attachmentId === file.attachment.attachmentId)?.thumbnail ?? null;
};

/**
 * The file's own thumbnail, fetched if it takes that, else its bucket's icon.
 *
 * `width` and `labelled` are for the list rows, which want a small square rather
 * than a tile-width band, and have no room under a 40px thumb for the bucket's name —
 * the row already writes it out beside the filename.
 */
export const FileThumb: React.FC<{ file: FolderFile; height?: number; width?: number; labelled?: boolean }> = ({
  file,
  height = 96,
  width,
  labelled = true,
}) => {
  const { t } = useTranslation();
  const style = BUCKET_STYLE[file.bucket];

  // Only images have thumbnails at all - the backend generates one for an image
  // upload and nothing else - so a PDF never costs a request that could only come
  // back empty.
  const couldHaveOne =
    !file.attachment.thumbnail &&
    file.source.type === 'visit' &&
    (file.attachment.contentType ?? '').startsWith('image/');

  const [ref, inView] = useInView<HTMLDivElement>(couldHaveOne);
  const fetched = useVisitAttachmentThumbnail(file, couldHaveOne && inView);

  const thumbnail = file.attachment.thumbnail ?? fetched;

  if (thumbnail) {
    return (
      <img
        src={`data:image/jpeg;base64,${thumbnail}`}
        alt={file.attachment.filename}
        className={`rounded-lg object-cover ${width ? 'shrink-0' : 'w-full'}`}
        style={{ height, width }}
      />
    );
  }

  return (
    <div
      ref={ref}
      className={`flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed ${width ? 'shrink-0' : ''}`}
      style={{ height, width, background: style.bg, borderColor: `${style.color}55`, color: style.color }}
    >
      <span style={{ fontSize: labelled ? 20 : Math.round((width ?? height) * 0.45) }}>{style.icon}</span>
      {labelled && <span className="text-[10px] font-medium">{t(FOLDER_BUCKET_LABEL_KEYS[file.bucket])}</span>}
    </div>
  );
};

type DownloadProps = {
  /** Omitted where the file is only a link, as in the visit drawer's list. */
  onDownload?: (file: FolderFile) => void;
  isDownloading?: (file: FolderFile) => boolean;
};

/**
 * Saving the file without opening it first.
 *
 * It sits on the tile and on the row because that is where the reader already is,
 * and because a picture opens the slideshow rather than the drawer that used to be
 * the only place offering a download.
 */
const FileDownloadButton: React.FC<{ file: FolderFile; className?: string } & Required<DownloadProps>> = ({
  file,
  onDownload,
  isDownloading,
  className,
}) => {
  const { t } = useTranslation();
  const busy = isDownloading(file);

  return (
    <button
      type="button"
      title={t('client-card-file-download')}
      aria-label={`${t('client-card-file-download')}: ${file.attachment.filename}`}
      disabled={busy}
      onClick={() => onDownload(file)}
      className={
        'flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full border border-slate-200 ' +
        'bg-white/90 text-[13px] text-slate-500 shadow-sm backdrop-blur hover:border-sky-300 hover:text-sky-600 ' +
        (className ?? '')
      }
    >
      {busy ? <LoadingOutlined /> : <DownloadOutlined />}
    </button>
  );
};

export const FileRow: React.FC<{ file: FolderFile; onOpen: (file: FolderFile) => void } & DownloadProps> = ({
  file,
  onOpen,
  onDownload,
  isDownloading = () => false,
}) => {
  const { t, i18n } = useTranslation();

  return (
    // A row, not a button: the download is an action of its own and cannot be nested
    // inside the button that opens the file.
    <div className="flex items-center gap-2 border-b border-slate-100 pr-3 last:border-0 hover:bg-slate-50">
      <button
        type="button"
        onClick={() => onOpen(file)}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 border-0 bg-transparent px-3 py-2.5 text-left"
      >
        {/* The picture, not just the bucket's icon: a folder of scans is told apart by
            what the pages look like, and the list view was the one place that hid them. */}
        <FileThumb file={file} height={40} width={40} labelled={false} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium text-slate-800">
            {file.attachment.description || file.attachment.filename}
          </div>
          <div className="truncate text-[11px] text-slate-500">
            {[
              t(FOLDER_BUCKET_LABEL_KEYS[file.bucket]),
              formatDate(file.at, i18n.language),
              file.source.type === 'visit' ? t('client-card-folder-from-visit') : t('client-card-folder-from-client'),
            ].join(' · ')}
          </div>
        </div>
      </button>
      {onDownload && <FileDownloadButton file={file} onDownload={onDownload} isDownloading={isDownloading} />}
    </div>
  );
};

/**
 * What a file is, as a badge in its own colour.
 *
 * Solid and white-on-colour rather than the tinted tag used elsewhere, because this
 * sits ON a photograph: a pale background cannot be trusted over an unknown image,
 * and every bucket colour in the palette is dark enough to carry white text. The
 * colour is the bucket's own, which is what ties a tile to the filter chip above it.
 */
const BucketBadge: React.FC<{ bucket: FolderBucket }> = ({ bucket }) => {
  const { t } = useTranslation();
  const style = BUCKET_STYLE[bucket];

  return (
    <span
      className="inline-flex max-w-full items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium text-white shadow-sm"
      style={{ background: style.color }}
    >
      <span className="text-[9px] leading-none">{style.icon}</span>
      <span className="truncate">{t(FOLDER_BUCKET_LABEL_KEYS[bucket])}</span>
    </span>
  );
};

const FileTile: React.FC<{ file: FolderFile; onOpen: (file: FolderFile) => void } & Required<DownloadProps>> = ({
  file,
  onOpen,
  onDownload,
  isDownloading,
}) => {
  const { i18n } = useTranslation();

  return (
    <div className="relative rounded-xl border border-slate-200 bg-white hover:border-sky-300">
      <button
        type="button"
        onClick={() => onOpen(file)}
        className="block w-full cursor-pointer border-0 bg-transparent p-2 text-left"
      >
        {/* The badge replaces the placeholder's caption rather than joining it, so every
            tile states its type exactly once whether or not it has a picture. */}
        <div className="relative">
          <FileThumb file={file} labelled={false} />
          <span className="absolute bottom-1.5 left-1.5 right-1.5 flex justify-end">
            <BucketBadge bucket={file.bucket} />
          </span>
        </div>
        <div className="mt-2 truncate text-[12px] font-medium text-slate-800">
          {file.attachment.description || file.attachment.filename}
        </div>
        <div className="truncate text-[11px] text-slate-500">{formatDate(file.at, i18n.language)}</div>
      </button>
      {/* Opposite corner from the badge, and always drawn rather than shown on hover:
          a tablet has no hover, and this is the tile's only way to save the file. */}
      <FileDownloadButton
        file={file}
        onDownload={onDownload}
        isDownloading={isDownloading}
        className="absolute right-3.5 top-3.5"
      />
    </div>
  );
};

type ClientFolderPanelProps = {
  clientId: number;
  clientAttachments: CardAttachment[];
  visits: CardVisit[];
  onOpenFile: (file: FolderFile) => void;
  isLoading?: boolean;
  /** The client-attachments fetch failed; the folder is incomplete, not empty. */
  hasError?: boolean;
  className?: string;
};

/**
 * Every file this client has, from their own record and from their visits.
 *
 * Filter chips are built from what is in the folder, so a client with only photos
 * gets two chips rather than eight, and the `kind` chips appear as soon as
 * something starts setting that column.
 */
const ClientFolderPanel: React.FC<ClientFolderPanelProps> = ({
  clientId,
  clientAttachments,
  visits,
  onOpenFile,
  isLoading,
  hasError,
  className,
}) => {
  const { t } = useTranslation();
  const [bucket, setBucket] = useState<FolderBucket | 'all'>('all');
  const [mode, setMode] = useState<'grid' | 'list'>('grid');
  // The panel's own downloader: the card's belongs to the drawer, and a tile and a
  // drawer can be waiting on different files at once.
  const { download, isDownloading } = useFolderDownload();

  const files = buildFolder(clientId, clientAttachments, visits);
  const counts = folderBucketCounts(files);

  // A refetch can empty the bucket the reader had selected. Derived rather than
  // reset in an effect, so there is no frame where a chip nothing matches is still
  // highlighted above an unexplained empty grid.
  const activeBucket = bucket !== 'all' && !counts.some((entry) => entry.bucket === bucket) ? 'all' : bucket;
  const shown = activeBucket === 'all' ? files : files.filter((file) => file.bucket === activeBucket);

  // The slideshow walks the images the reader is looking at, so the filter chips
  // narrow it the same way they narrow the grid.
  const images = shown.filter(isFolderImage);
  const preview = useFolderPreview(images);

  // A picture opens as a picture; everything else has nothing to show full-screen
  // and keeps the drawer that names it and offers the download.
  const openFile = (file: FolderFile) => (isFolderImage(file) ? preview.open(file) : onOpenFile(file));

  const chips: Array<{ key: FolderBucket | 'all'; label: string; count: number }> = [
    { key: 'all', label: t('client-card-folder-all'), count: files.length },
    ...counts.map((entry) => ({
      key: entry.bucket,
      label: t(FOLDER_BUCKET_LABEL_KEYS[entry.bucket]),
      count: entry.count,
    })),
  ];

  return (
    <Panel
      title={t('client-card-folder')}
      extra={
        files.length > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('client-card-folder-filters')}>
              {chips.map((chip) => {
                const selected = activeBucket === chip.key;
                // Each bucket's chip wears that bucket's colour, so the reader can
                // match a chip to the badges on the tiles without reading either.
                // Selected is the solid colour the badge uses; unselected is its tint.
                const tone = chip.key === 'all' ? null : BUCKET_STYLE[chip.key];

                return (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setBucket(chip.key)}
                    aria-pressed={selected}
                    className={
                      'cursor-pointer rounded-full border px-2.5 py-1 text-[12px] ' +
                      (selected ? 'text-white' : tone ? '' : 'border-slate-200 bg-white text-slate-600') +
                      (selected ? '' : ' hover:opacity-80')
                    }
                    style={
                      selected
                        ? { background: tone?.color ?? ALL_CHIP_COLOR, borderColor: tone?.color ?? ALL_CHIP_COLOR }
                        : tone
                          ? { background: tone.bg, color: tone.color, borderColor: `${tone.color}44` }
                          : undefined
                    }
                  >
                    {chip.label} <span className="tabular-nums opacity-70">{chip.count}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex shrink-0 overflow-hidden rounded-lg border border-slate-200">
              {(
                [
                  ['grid', <AppstoreOutlined key="grid" />, t('client-card-folder-grid')],
                  ['list', <UnorderedListOutlined key="list" />, t('client-card-folder-list')],
                ] as const
              ).map(([key, icon, label]) => (
                <button
                  key={key}
                  type="button"
                  title={label}
                  aria-label={label}
                  onClick={() => setMode(key)}
                  className={
                    'flex cursor-pointer items-center gap-1.5 border-0 px-2.5 py-1 text-[12px] ' +
                    (mode === key ? 'text-white' : 'bg-white text-slate-600 hover:bg-slate-50')
                  }
                  style={mode === key ? { background: 'rgb(2 132 199)' } : undefined}
                >
                  {icon}
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
          </div>
        )
      }
      className={className}
      pad={mode === 'list' && files.length > 0 ? 'p-0' : 'p-4'}
    >
      {hasError ? (
        // Never "there are no files": a coordinator would conclude the scans were
        // never uploaded, which is the failure this page avoids everywhere else.
        <Alert type="error" showIcon message={t('client-card-folder-error')} />
      ) : files.length === 0 ? (
        <div className="py-10 text-center text-[13px] text-slate-400">
          {isLoading ? t('common-loading') : t('client-card-folder-empty')}
        </div>
      ) : mode === 'grid' ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {shown.map((file) => (
            <FileTile
              key={folderFileKey(file)}
              file={file}
              onOpen={openFile}
              onDownload={download}
              isDownloading={isDownloading}
            />
          ))}
        </div>
      ) : (
        <div>
          {shown.map((file) => (
            <FileRow
              key={folderFileKey(file)}
              file={file}
              onOpen={openFile}
              onDownload={download}
              isDownloading={isDownloading}
            />
          ))}
        </div>
      )}

      {/* No children: the tiles above are the triggers, and this only carries the
          full-screen viewer they open. */}
      {preview.groupProps.items.length > 0 && (
        <Image.PreviewGroup
          {...preview.groupProps}
          preview={{
            ...preview.groupProps.preview,
            // Appended to antd's own toolbar rather than replacing it, so zoom and
            // rotate stay put and the download joins them for the picture on screen.
            toolbarRender: (originalNode, info) => {
              const toolbar = originalNode as React.ReactElement<{ children?: React.ReactNode }>;
              const current = images[info.current];

              return React.cloneElement(toolbar, {}, [
                ...React.Children.toArray(toolbar.props.children),
                <div
                  key="download"
                  role="button"
                  aria-label={t('client-card-file-download')}
                  title={t('client-card-file-download')}
                  className="ant-image-preview-operations-operation"
                  onClick={() => current && download(current)}
                >
                  {current && isDownloading(current) ? <LoadingOutlined /> : <DownloadOutlined />}
                </div>,
              ]);
            },
          }}
        />
      )}
    </Panel>
  );
};

export default ClientFolderPanel;
